import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, Search, X } from "lucide-react";
import type NodeDescriptor from "../../models/NodeDescriptor";
import { renderNodeIcon, getNodeSolidColor } from "./nodeIcons";
import { AUTOMATION_NODE_DRAG_TYPE } from "./AutomationCanvas";
import { descriptorKey, readRecentNodeKeys } from "../../lib/recentNodesStorage";

interface NodePaletteProps {
  catalog: NodeDescriptor[];
  loading: boolean;
  open: boolean;
  onClose: () => void;
}

/** Accent/case-insensitive normalization so "integracion" matches "Integración". */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export default function NodePalette({ catalog, loading, open, onClose }: NodePaletteProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [recentKeys, setRecentKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setRecentKeys(readRecentNodeKeys());
    searchRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return catalog;
    return catalog.filter((descriptor) =>
      normalize(`${descriptor.displayName} ${descriptor.description} ${descriptor.category}`).includes(q),
    );
  }, [catalog, query]);

  const recent = useMemo(() => {
    if (recentKeys.length === 0) return [];
    const byKey = new Map(filtered.map((d) => [descriptorKey(d), d]));
    return recentKeys.map((key) => byKey.get(key)).filter((d): d is NodeDescriptor => d !== undefined);
  }, [filtered, recentKeys]);

  const grouped = useMemo(() => {
    const map = new Map<string, NodeDescriptor[]>();
    filtered.forEach((descriptor) => {
      const list = map.get(descriptor.category) ?? [];
      list.push(descriptor);
      map.set(descriptor.category, list);
    });
    return [...map.entries()];
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    function handler(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, descriptor: NodeDescriptor) => {
    event.dataTransfer.setData(AUTOMATION_NODE_DRAG_TYPE, JSON.stringify(descriptor));
    event.dataTransfer.effectAllowed = "move";
  };

  if (!open) return null;

  const renderItem = (descriptor: NodeDescriptor) => (
    <div
      key={descriptorKey(descriptor)}
      draggable
      onDragStart={(event) => handleDragStart(event, descriptor)}
      title={descriptor.description}
      className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/70 border border-black/5 cursor-grab active:cursor-grabbing hover:border-accent/30 hover:bg-accent/5 transition-all"
    >
      {renderNodeIcon(
        descriptor.nodeType,
        `size-4 shrink-0 ${descriptor.icon ? "" : getNodeSolidColor(descriptor.nodeType).text}`,
        descriptor.icon,
      )}
      <span className="text-sm font-semibold text-foreground truncate">
        {descriptor.displayName}
      </span>
    </div>
  );

  return (
    <div
      ref={panelRef}
      className="absolute top-4 left-4 z-30 w-72 max-h-[calc(100%-2rem)] overflow-y-auto rounded-2xl border border-black/5 bg-white/90 backdrop-blur-md shadow-2xl p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-bold uppercase tracking-widest text-foreground/40">
          Nodos disponibles
        </h2>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-black/5 text-foreground/40 hover:text-foreground transition-colors cursor-pointer"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-foreground/30 pointer-events-none" />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") onClose();
          }}
          placeholder="Buscar nodos..."
          className="w-full pl-8 pr-3 py-2 rounded-xl bg-white/70 border border-black/5 text-sm text-foreground placeholder:text-foreground/30 outline-none focus:border-accent/40 transition-colors"
        />
      </div>

      {loading && <p className="text-sm text-foreground/40">Cargando catálogo...</p>}

      {!loading && catalog.length === 0 && (
        <p className="text-sm text-foreground/40">No hay tipos de nodo disponibles.</p>
      )}

      {!loading && catalog.length > 0 && filtered.length === 0 && (
        <p className="text-sm text-foreground/40">Sin resultados para "{query.trim()}".</p>
      )}

      {recent.length > 0 && (
        <div className="mb-4">
          <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-foreground/30 mb-2">
            <Clock className="size-3" />
            Recientes
          </p>
          <div className="space-y-2">{recent.map(renderItem)}</div>
        </div>
      )}

      {grouped.map(([category, descriptors]) => (
        <div key={category} className="mb-4 last:mb-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/30 mb-2">
            {category}
          </p>
          <div className="space-y-2">{descriptors.map(renderItem)}</div>
        </div>
      ))}
    </div>
  );
}
